import React, { forwardRef, useMemo, useRef, useEffect, useState, useImperativeHandle } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Table, Checkbox, Alert } from 'components/ui' // Removed Pagination/Select from import if unused in this file, kept if needed
import TableRowSkeleton from './loaders/TableRowSkeleton'
import Loading from './Loading'
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table'

const { Tr, Th, Td, THead, TBody, TFoot, Sorter } = Table

const IndeterminateCheckbox = ((props) => {
    const { indeterminate, onChange, onCheckBoxChange, onIndeterminateCheckBoxChange, ...rest } = props
    const ref = useRef(null)

    useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, indeterminate])

    const handleChange = (e) => {
        onChange(e)
        onCheckBoxChange?.(e)
        onIndeterminateCheckBoxChange?.(e)
    }

    return <Checkbox className="mb-0" ref={ref} onChange={(_, e) => handleChange(e)} {...rest} />
})

const DataTable = forwardRef((props, ref) => {
    const {
        skeletonAvatarColumns,
        columns: columnsProp,
        data,
        loading,
        onCheckBoxChange,
        onIndeterminateCheckBoxChange,
        onPaginationChange,
        onSelectChange,
        onSort,
        pageSizes,
        selectable,
        skeletonAvatarProps,
        pagination,
        pagingData,
        hasSorting,
        showFooter, // 2. NEW PROP: Controls footer visibility
    } = props

    const { pageSize, pageIndex, total } = pagingData
    const [sorting, setSorting] = useState(null)

    const pageSizeOption = useMemo(
        () =>
            pageSizes.map((number) => ({
                value: number,
                label: `${number} / page`,
            })),
        [pageSizes]
    )

    const handleCheckBoxChange = (checked, row) => {
        if (!loading) {
            onCheckBoxChange?.(checked, row)
        }
    }

    const handleIndeterminateCheckBoxChange = (checked, rows) => {
        if (!loading) {
            onIndeterminateCheckBoxChange?.(checked, rows)
        }
    }

    useEffect(() => {
        if (Array.isArray(sorting)) {
            const sortOrder =  sorting.length > 0 ? sorting[0].desc ?  'desc' : 'asc' : ''
            const id = sorting.length > 0 ? sorting[0].id : ''
            onSort?.({ order: sortOrder, key: id })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting])

    const hasOldColumnMetaKey = columnsProp.some(col => col.Header || col.accessor || col.Cell)

    const finalColumns = useMemo(() => {
        const columns = columnsProp
        if (selectable) {
            return [
                {
                    id: 'select',
                    header: ({ table }) => (
                        <IndeterminateCheckbox
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                            onIndeterminateCheckBoxChange={(e) => {
                                handleIndeterminateCheckBoxChange(
                                    e.target.checked,
                                    table.getRowModel().rows
                                )
                            }}
                        />
                    ),
                    cell: ({ row }) => (
                        <IndeterminateCheckbox
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            indeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                            onCheckBoxChange={(e) =>
                                handleCheckBoxChange(
                                    e.target.checked,
                                    row.original
                                )
                            }
                        />
                    ),
                },
                ...columns
            ]
        }
        return columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsProp, selectable])


    const table = useReactTable({
        data,
        columns: hasOldColumnMetaKey ? [] : finalColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        manualSorting: true,
        onSortingChange: setSorting,
        state: {
            sorting,
        },
    })

    const resetSorting = () => {
        table.resetSorting()
    };

    const resetSelected = () => {
        table.toggleAllRowsSelected(false);
    }

    useImperativeHandle(ref, () => ({
        resetSorting,
        resetSelected
    }));

    if (hasOldColumnMetaKey) {
        const message = 'You are using old react-table v7 column config, please use v8 column config instead, refer to our demo or https://tanstack.com/table/v8'
        if (process.env.NODE_ENV === 'development') {
            console.warn(message)
        }
        return <Alert>{message}</Alert>
    }

    const startingIndex = (pageIndex - 1) * pageSize;
    const endingIndex = pageIndex * pageSize;

    return (
        <Loading loading={loading && data.length !== 0} type="cover">
            <Table className="br-8">
                <THead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <Th key={header.id} colSpan={header.colSpan}>
                                        {header.isPlaceholder ? null : (
                                            <div
                                                className={
                                                    classNames(
                                                        (header.column.getCanSort() ) && 'cursor-pointer select-none point',
                                                        loading && 'pointer-events-none'
                                                    )
                                                }
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {/* Status columns hide the sorter by default - most of them
                                                    render a badge off a code the server doesn't order by. A
                                                    column opts in with meta: { sortable: true }. */}
                                                {header.column.getCanSort()
                                                    && (header.column.id !== "status"
                                                        || header.column.columnDef.meta?.sortable === true)
                                                    && <Sorter sort={header.column.getIsSorted()} />}
                                            </div>
                                        )}
                                    </Th>
                                )
                            })}
                        </Tr>
                    ))}
                </THead>
                {loading && data.length === 0 ? (
                    <TableRowSkeleton
                        columns={finalColumns.length}
                        rows={pagingData.pageSize}
                        avatarInColumns={skeletonAvatarColumns}
                        avatarProps={skeletonAvatarProps}
                    />
                ) : (
                    <>
                        <TBody>
                            {table
                                .getRowModel()
                                .rows.sort((a, b) => {
                                    if (hasSorting && sorting && sorting.length > 0 && sorting[0].id !== "views") {
                                        const key = sorting[0].id
                                        const aVal = a.original[key]
                                        const bVal = b.original[key]

                                        if (aVal == null) return 1
                                        if (bVal == null) return -1

                                        if (typeof aVal === "number" && typeof bVal === "number") {
                                            return sorting[0].desc ? bVal - aVal : aVal - bVal
                                        }

                                        return sorting[0].desc
                                        ? String(bVal).localeCompare(String(aVal))
                                        : String(aVal).localeCompare(String(bVal))
                                    }
                                    return 0;
                                })
                                .map((row,i) => {
                                    if (i >= startingIndex && i < endingIndex){
                                        return (
                                            <Tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => {
                                                    return (
                                                        <Td key={cell.id}>
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </Td>
                                                    )
                                                })}
                                            </Tr>
                                        )
                                    }
                                    return null;
                                })}
                        </TBody>

                        {/* 3. OPTIONAL FOOTER RENDERING */}
                        {showFooter && (
                            <TFoot className="bg-gray-50 border-t-2 border-gray-100">
                                {table.getFooterGroups().map((footerGroup) => (
                                    <Tr key={footerGroup.id}>
                                        {footerGroup.headers.map((header) => (
                                            <Td key={header.id} className="font-bold text-gray-800 py-4">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                          header.column.columnDef.footer,
                                                          header.getContext()
                                                      )}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </TFoot>
                        )}
                    </>
                )}
            </Table>
        </Loading>
    )
})

DataTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    onCheckBoxChange: PropTypes.func,
    onIndeterminateCheckBoxChange: PropTypes.func,
    onPaginationChange: PropTypes.func,
    onSelectChange: PropTypes.func,
    onSort: PropTypes.func,
    pageSizes: PropTypes.arrayOf(PropTypes.number),
    selectable: PropTypes.bool,
    skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
    skeletonAvatarProps: PropTypes.object,
    pagingData: PropTypes.shape({
        total: PropTypes.number,
        pageIndex: PropTypes.number,
        pageSize: PropTypes.number,
    }),
    hasSorting: PropTypes.bool,
    showFooter: PropTypes.bool, // Added to PropTypes
}

DataTable.defaultProps = {
    pageSizes: [10, 25, 50, 100],
    pagingData: {
        total: 0,
        pageIndex: 1,
        pageSize: 10,
    },
    data: [],
    columns: [],
    selectable: false,
    loading: false,
    showFooter: false, // Default is false to prevent regression
}

export default DataTable